import React, { useRef } from 'react'
import { Div, Flex, Text } from '@sendoutcards/quantum-design-ui'
import { FontFragment } from 'src/graphql/generated/graphql'
import { useEffect, useState } from 'src/hooks'

type FontSelectionSettingsProps = {
  fonts?: FontFragment[]
  signatures?: FontFragment[]
  currentFontSettings: { fontFamily: string; signatureId?: string }
  onFontSettingSelection: (
    key: 'signatureId' | 'fontFamily',
    value: string,
  ) => void
}

const FontDropdown = ({
  label,
  options,
  selectedValue,
  onChange,
  isSignatureDropdown = false,
}: {
  label: string
  options: FontFragment[]
  selectedValue: string
  onChange: (font: FontFragment) => void
  isSignatureDropdown?: boolean
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const selectedFont =
    options.find(
      f => f.fontName === selectedValue || f.displayName === selectedValue,
    ) || options[0]

  // Load fonts dynamically
  useEffect(() => {
    const loadFonts = async () => {
      for (const font of options) {
        if (font.ttfFile && font.fontName) {
          try {
            const fontFace = new FontFace(font.fontName, `url(${font.ttfFile})`)
            const loadedFace = await fontFace.load()
            document.fonts.add(loadedFace)
          } catch (err) {
            console.error(`Failed to load font ${font.fontName}:`, err)
          }
        }
      }
    }

    loadFonts()
  }, [options])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <Div style={dropdownGroupStyle}>
      <Text type="caption" style={labelStyle}>
        {label}
      </Text>
      <div ref={dropdownRef} style={dropdownContainerStyle}>
        <button style={dropdownButtonStyle} onClick={() => setIsOpen(!isOpen)}>
          <span
            style={{
              fontFamily: selectedFont.fontName,
              fontSize: isSignatureDropdown ? '32px' : '15px',
            }}
          >
            {isSignatureDropdown ? 'æ' : selectedFont.displayName}
          </span>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            style={{
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          >
            <path
              d="M4 6L8 10L12 6"
              stroke="#9CA3AF"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {isOpen && (
          <div style={dropdownMenuStyle}>
            {options.map((font, index) => {
              const isSelected = font.fontName === selectedFont.fontName
              return (
                <button
                  key={index}
                  style={{
                    ...dropdownItemStyle,
                    fontFamily: font.fontName,
                    fontSize: isSignatureDropdown ? '40px' : '15px',
                    backgroundColor: isSelected ? '#8B5CF6' : 'white',
                    color: isSelected ? 'white' : '#374151',
                    fontWeight: isSelected ? '500' : '400',
                    boxShadow: isSelected
                      ? '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)'
                      : 'none',
                  }}
                  onMouseEnter={e => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = '#F9FAFB'
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isSelected) {
                      e.currentTarget.style.backgroundColor = 'white'
                    }
                  }}
                  onClick={() => {
                    onChange(font)
                    setIsOpen(false)
                  }}
                  disabled={font.isDisabled}
                >
                  {isSignatureDropdown ? 'æ' : font.displayName}
                </button>
              )
            })}
          </div>
        )}
      </div>
    </Div>
  )
}

export const FontSelectionSettings = ({
  currentFontSettings,
  onFontSettingSelection,
  fonts,
  signatures,
}: FontSelectionSettingsProps) => {
  const availableFonts = fonts?.filter(font => !font.isDisabled) || []
  // const availableSignatures = signatures?.filter(sig => !sig.isDisabled) || []

  return (
    <Flex style={dropdownRowStyle}>
      {/* Font Family Dropdown */}
      <FontDropdown
        label="Typeface"
        options={availableFonts}
        selectedValue={currentFontSettings.fontFamily}
        onChange={font => onFontSettingSelection('fontFamily', font.fontName)}
        isSignatureDropdown={false}
      />
      {/* Signature/Style Dropdown */}
      {/* availableSignatures.length > 0 && (
        <FontDropdown
          label="Style"
          options={availableSignatures}
          selectedValue={currentFontSettings.signatureId || ''}
          onChange={font => onFontSettingSelection('signatureId', font.id)}
          isSignatureDropdown={true}
        />
      )}*/}
    </Flex>
  )
}

const labelStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#6B7280',
  marginBottom: '8px',
  fontWeight: 'normal',
}

const dropdownRowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '16px',
  marginTop: '2rem',
}

const dropdownGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
}

const dropdownContainerStyle: React.CSSProperties = {
  position: 'relative',
  width: '100%',
}

const dropdownButtonStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  backgroundColor: 'white',
  border: '1px solid #E5E7EB',
  borderRadius: '8px',
  cursor: 'pointer',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontSize: '15px',
  textAlign: 'left',
  fontWeight: '400',
  color: '#1F2937',
}

const dropdownMenuStyle: React.CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  marginTop: '4px',
  backgroundColor: 'white',
  border: '1px solid #E5E7EB',
  borderRadius: '12px',
  boxShadow:
    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  maxHeight: '300px',
  overflowY: 'auto',
  zIndex: 1000,
  padding: '4px',
}

const dropdownItemStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: 'none',
  backgroundColor: 'white',
  cursor: 'pointer',
  textAlign: 'left',
  fontSize: '15px',
  borderRadius: '8px',
  transition: 'all 0.15s ease',
  color: '#374151',
  fontWeight: '400',
}
